import type { TwnPaletteKeyHintsItemData } from '@/components/molecules/palette/twn-palette-key-hints/TwnPaletteKeyHints.types'
import type { StockModeStep, StockSideTrip } from '@/domains/command-center/handlers/stock/stockMode.types'
import type { AdjustmentMode } from '@/domains/quick-updates/types/models/QuickInventoryUpdate.types'

import { PALETTE_DEFAULT_HINTS } from '@/components/organisms/twn-palette-dialog/TwnPaletteDialog.consts'

const STOCK_CATEGORY_ID = 'cat-stock'

/** The plain command: stock mode with no item chosen yet. */
const UPDATE_STOCK_ACTION_ID = 'action-update-stock'

/** The row a scan earns: stock mode with the scanned product already chosen. */
const UPDATE_STOCK_FOR_MATCH_ID = 'action-update-stock-for-match'

/** The line a refused save leaves above the rows. A screen reader hears it as an alert, not a status. */
const STOCK_ERROR_ROW_ID = 'stock-error'

/**
 * Six digits is 999,999 units, and no stock change is that large. Most barcodes are longer, so a seventh digit
 * reads as a scanner typing. A short code can still pass as a number, which is why the scanned code is taken
 * back out of the box and a scanner's Enter is refused, rather than trusting this alone.
 */
const MAX_QUANTITY_DIGITS = 6

/** The sign a merchant types to pick a row, and the one the box shows back. */
const MODE_SIGNS: Record<AdjustmentMode, string> = {
  add: '+',
  remove: '-',
  set: '='
}

/** `−` is the true minus sign. No keyboard types it, but a paste can carry it. */
const SIGN_MODES = {
  '+': 'add',
  '-': 'remove',
  '−': 'remove',
  '=': 'set'
} as const satisfies Record<string, AdjustmentMode>

const MODE_ICONS: Record<AdjustmentMode, string> = {
  add: 'add',
  remove: 'remove',
  set: 'inventory'
}

const MODE_VERBS: Record<AdjustmentMode, string> = {
  add: 'Add',
  remove: 'Remove',
  set: 'Set to'
}

/** The heading over each mode's reasons. */
const REASON_HEADINGS: Record<AdjustmentMode, string> = {
  add: 'Why is stock coming in?',
  remove: 'Why is stock going out?',
  set: 'Why is the count changing?'
}

/** The steps a side trip can return to. */
const MAIN_STEPS: readonly StockModeStep[] = ['item', 'variant', 'quantity']

/** The side trips: each returns to the step it was opened from. `StockSideTrip` is derived from this list. */
const SIDE_TRIP_STEPS = ['reason', 'location'] as const

/** The steps whose rows are a fixed list, narrowed by what the merchant types. */
const LOCALLY_FILTERED_STEPS: readonly StockModeStep[] = ['variant', 'reason', 'location']

const STEP_PLACEHOLDERS: Record<StockModeStep, string> = {
  item: 'Scan or search a product',
  variant: 'Search the variants',
  quantity: 'How many?',
  reason: 'Why is the stock changing?',
  location: 'Choose a location'
}

/** The icons the other rows lead with, beside the per-mode ones above. */
const ROW_ICONS = {
  item: 'inventory_2',
  location: 'location_on',
  reason: 'edit_note',
  chosen: 'check',
  saving: 'hourglass_empty',
  saved: 'check_circle'
} as const

/** The palette's own Navigate hint, so stock mode and command mode read the same. */
const NAVIGATE_HINT = PALETTE_DEFAULT_HINTS[0]

const BACK_HINT: TwnPaletteKeyHintsItemData = { keys: ['⌫'], label: 'Back' }

const STEP_HINTS: Record<StockModeStep, readonly TwnPaletteKeyHintsItemData[]> = {
  item: [NAVIGATE_HINT, { keys: ['↵'], label: 'Choose' }, { keys: ['Tab'], label: 'Location' }, BACK_HINT],
  variant: [NAVIGATE_HINT, { keys: ['↵'], label: 'Choose' }, BACK_HINT],
  quantity: [{ keys: ['+', '-', '='], label: 'Pick a row' }, { keys: ['↵'], label: 'Save' }, BACK_HINT],
  reason: [NAVIGATE_HINT, { keys: ['↵'], label: 'Use this reason' }, BACK_HINT],
  location: [NAVIGATE_HINT, { keys: ['↵'], label: 'Use this location' }, BACK_HINT]
}

/** The quantity step's hints while its change is on the way. Enter has nothing to do until the server answers. */
const SAVING_HINTS: readonly TwnPaletteKeyHintsItemData[] = [{ keys: ['↵'], label: 'Saving…' }, BACK_HINT]

/** The mode's name: the command that opens it, and the crumb's root. */
const STOCK_MODE_NAME = 'Update Stock'

/** What the crumb adds after the mode's name on a side trip. The other steps name their item in the heading. */
const SIDE_TRIP_CRUMBS: Record<StockSideTrip, string> = {
  reason: 'Reason',
  location: 'Location'
}

export {
  LOCALLY_FILTERED_STEPS,
  MAIN_STEPS,
  MAX_QUANTITY_DIGITS,
  MODE_ICONS,
  MODE_SIGNS,
  MODE_VERBS,
  REASON_HEADINGS,
  ROW_ICONS,
  SAVING_HINTS,
  SIDE_TRIP_CRUMBS,
  SIDE_TRIP_STEPS,
  SIGN_MODES,
  STEP_HINTS,
  STEP_PLACEHOLDERS,
  STOCK_CATEGORY_ID,
  STOCK_ERROR_ROW_ID,
  STOCK_MODE_NAME,
  UPDATE_STOCK_ACTION_ID,
  UPDATE_STOCK_FOR_MATCH_ID
}
